import { useQuery } from "@apollo/client";
import { Button, Container, Heading, Table } from "@fluidattacks/design";
import { Fragment, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { GET_TOE_LANGUAGES } from "./queries";
import type { ICodeLanguage } from "./types";

import { useAudit } from "hooks";
import { useTable } from "hooks/use-table";
import { formatPercentage } from "utils/format-helpers";
import { Logger } from "utils/logger";

export const GroupToeLanguages: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { groupName } = useParams() as { groupName: string };
  const { addAuditEvent } = useAudit();
  const [search, setSearch] = useState("");

  const { data, loading } = useQuery(GET_TOE_LANGUAGES, {
    onCompleted: (): void => {
      addAuditEvent("Group.ToeLanguages", groupName);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load group toe languages", error);
      });
    },
    variables: { groupName },
  });

  const languages = data?.group.codeLanguages ?? [];
  const totalLoc = languages.reduce(
    (total, language): number => total + language.loc,
    0,
  );
  const completeData: ICodeLanguage[] = languages.map(
    (lang): ICodeLanguage => ({
      id: lang.language,
      language: lang.language,
      loc: lang.loc,
      percentage: lang.loc / totalLoc,
    }),
  );
  const filteredData = useMemo((): ICodeLanguage[] => {
    return completeData.filter(({ language }): boolean =>
      language.toLowerCase().includes(search.toLowerCase()),
    );
  }, [completeData, search]);

  const { table } = useTable({
    columns: [
      {
        accessorKey: "language",
        header: t("group.toe.codeLanguages.lang"),
        size: 450,
      },
      {
        accessorKey: "loc",
        header: t("group.toe.codeLanguages.loc"),
        size: 450,
      },
      {
        accessorKey: "percentage",
        cell: (cell): string => formatPercentage(Number(cell.getValue()), true),
        header: t("group.toe.codeLanguages.percent"),
      },
    ],
    data: filteredData,
    id: "code-languages-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      loading,
      paginationMode: "client-side",
    },
    total: filteredData.length,
  });

  return (
    <Fragment>
      <Heading fontWeight={"bold"} mb={1.25} size={"sm"}>
        {t("group.toe.codeLanguages.title")}
      </Heading>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Container id={"actions-container"}>
              <Table.Export csvConfig={{ export: true }} data={filteredData}>
                <Button
                  icon={"file-export"}
                  id={"CSVExportBtn"}
                  tooltip={t("buttons.exportTip", { format: "CSV" })}
                  variant={"ghost"}
                >
                  {t("group.findings.exportCsv.text")}
                </Button>
              </Table.Export>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
    </Fragment>
  );
};
